/**
 * Axios Interceptor Client
 *
 * Centralized HTTP client with comprehensive error handling, logging,
 * and request/response transformation.
 *
 * Features:
 * - Request/response logging (debug mode)
 * - Automatic error transformation
 * - Retry logic for transient failures
 * - Request timeout management
 * - Credential handling
 * - Launch context injection
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios, { isAxiosError as axiosIsAxiosError } from 'axios';
import { config } from '../config/environment';
import { getInstanceId } from '../services/scopedStorage';
/**
 * HTTP methods that are inherently safe to retry (idempotent or read-only).
 */
const SAFE_RETRY_METHODS = new Set(['get', 'head', 'options']);
/**
 * POST path prefixes for loader-service endpoints that are idempotent and safe to retry.
 * EHR pass-through endpoints (e.g. /ehr/orders/writeback, /consult-orders) are excluded.
 */
const DEFAULT_RETRYABLE_POST_PREFIXES = [
    '/api/screener/patients/', // covers /screener/patients/resources
    '/api/screener/reports/', // covers /screener/reports/referral-order/patient/{id}
    '/api/screener/providers/', // covers /screener/providers/{id}/.../reports/{id}/viewed
    '/api/screener/alerts' // covers /screener/alerts
];
export function resolveRequestPath(url, baseURL) {
    if (!url) {
        return null;
    }
    try {
        if (/^https?:\/\//i.test(url)) {
            return new URL(url).pathname;
        }
        const resolvedBaseUrl = new URL(baseURL || window.location.origin);
        const basePath = resolvedBaseUrl.pathname.replace(/\/$/, '');
        const requestPath = url.startsWith('/') ? url : `/${url}`;
        const resolvedUrl = new URL(`${basePath}${requestPath}`, resolvedBaseUrl.origin);
        return resolvedUrl.pathname;
    }
    catch (_a) {
        return null;
    }
}
/**
 * Create an ApiError with comprehensive error details.
 */
export function createApiError(message, statusCode, response, isNetworkError = false, isTimeout = false) {
    const error = new Error(message);
    // Define properties to match the interface
    Object.defineProperties(error, {
        name: { value: 'ApiError', writable: false },
        statusCode: { value: statusCode, writable: false },
        response: { value: response, writable: false },
        data: { value: response === null || response === void 0 ? void 0 : response.data, writable: false },
        isNetworkError: { value: isNetworkError, writable: false },
        isTimeout: { value: isTimeout, writable: false },
        isSessionExpired: { value: statusCode === 401, writable: false },
        isNotFound: { value: statusCode === 404, writable: false },
        isConflict: { value: statusCode === 409, writable: false },
        isValidationError: { value: statusCode === 400, writable: false },
        isServerError: { value: statusCode !== undefined && statusCode >= 500, writable: false }
    });
    return error;
}
/**
 * Delay utility for retry logic
 */
function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/**
 * Extract error message from various error response formats.
 * Handles different API response structures.
 */
function extractErrorMessageFromResponse(data, fallback) {
    if (!data || typeof data !== 'object') {
        return fallback;
    }
    const errorData = data;
    // Try different message properties in order of preference
    return errorData.message || errorData.errorDescription || errorData.error || fallback;
}
/**
 * Parse Blob error response to JSON.
 * When responseType is 'blob', error responses are also returned as Blobs.
 * Spring Boot global exception handler returns JSON errors that need to be parsed.
 */
function parseBlobError(blob) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const text = yield blob.text();
            return JSON.parse(text);
        }
        catch (_a) {
            return null;
        }
    });
}
/**
 * Transform Axios error to standardized ApiError format.
 * This is the ONLY place error transformation happens.
 * Services should NOT duplicate this logic.
 */
function transformError(error) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const isTimeout = error.code === 'ECONNABORTED' || ((_a = error.message) === null || _a === void 0 ? void 0 : _a.includes('timeout'));
        if (error.response) {
            const status = error.response.status;
            const data = error.response.data;
            // Handle Blob responses (responseType: 'blob')
            // When a request fails with responseType: 'blob', the error response is also a Blob
            if (data instanceof Blob) {
                const parsedError = yield parseBlobError(data);
                if (parsedError) {
                    const message = extractErrorMessageFromResponse(parsedError, error.message);
                    return createApiError(message, status, error.response, false, isTimeout);
                }
                // Fallback if parsing fails
                return createApiError('Request failed', status, error.response, false, isTimeout);
            }
            const errorData = data;
            const message = extractErrorMessageFromResponse(errorData, error.message);
            return createApiError(message, status, error.response, false, isTimeout);
        }
        if (error.request) {
            const message = isTimeout ? 'Request timed out' : 'Network error: No response from server';
            return createApiError(message, undefined, undefined, true, isTimeout);
        }
        return createApiError(error.message || 'Request failed', undefined, undefined, false, false);
    });
}
/**
 * Determine if a POST request's path is safe to retry.
 * Only POST requests to known idempotent loader-service endpoints are retryable.
 * EHR pass-through endpoints are NOT retried.
 */
function isRetryablePost(requestConfig, retryablePostPrefixes) {
    const pathname = resolveRequestPath(requestConfig.url, requestConfig.baseURL);
    if (!pathname) {
        return false;
    }
    return retryablePostPrefixes.some((prefix) => pathname.startsWith(prefix));
}
/**
 * Determine if request should be retried based on method, path, and error type.
 *
 * Retry policy:
 * - Transient HTTP status codes (408, 429, 502, 503, 504): retried for all methods
 *   (previous behavior, unchanged)
 * - Network/timeout errors: retried for GET/HEAD/OPTIONS, and for POST only when the
 *   path matches a known idempotent loader-service endpoint
 */
function shouldRetry(error, requestConfig, retryConfig) {
    var _a;
    if (!retryConfig.enabled) {
        return false;
    }
    const retryCount = requestConfig._retryCount || 0;
    if (retryCount >= retryConfig.maxRetries) {
        return false;
    }
    const method = (requestConfig.method || 'get').toLowerCase();
    const isNetworkOrTimeout = !error.response || error.code === 'ECONNABORTED' || ((_a = error.message) === null || _a === void 0 ? void 0 : _a.includes('timeout'));
    // Network/timeout errors: only retry safe methods and whitelisted POST paths
    if (isNetworkOrTimeout) {
        if (SAFE_RETRY_METHODS.has(method)) {
            return true;
        }
        if (method === 'post') {
            return isRetryablePost(requestConfig, retryConfig.retryablePostPrefixes);
        }
        return false;
    }
    // Transient HTTP status codes: retry for all methods
    const retryableStatuses = [408, 429, 502, 503, 504];
    return error.response != null && retryableStatuses.includes(error.response.status);
}
/**
 * Setup request interceptors on an axios instance
 */
function setupRequestInterceptors(instance, excludeBasicAuth = false) {
    instance.interceptors.request.use((requestConfig) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        requestConfig.headers = (_a = requestConfig.headers) !== null && _a !== void 0 ? _a : {};
        const launchContext = getInstanceId();
        if (launchContext) {
            requestConfig.headers['X-Launch-Context'] = launchContext;
        }
        const hasBasicAuth = !excludeBasicAuth && !!(config.basicAuthUsername && config.basicAuthPassword);
        if (hasBasicAuth) {
            const credentials = btoa(`${config.basicAuthUsername}:${config.basicAuthPassword}`);
            requestConfig.headers.Authorization = `Basic ${credentials}`;
        }
        // Add Accept-Language header based on current i18n language
        requestConfig.headers['Accept-Language'] = navigator.language || 'en';
        return requestConfig;
    }), (error) => {
        return Promise.reject(error);
    });
}
/**
 * Setup response interceptors on an axios instance
 */
function setupResponseInterceptors(instance, retryConfig) {
    instance.interceptors.response.use((response) => {
        return response;
    }, (error) => __awaiter(this, void 0, void 0, function* () {
        return handleResponseError(instance, error, retryConfig);
    }));
}
/**
 * Handle response errors with retry logic and error transformation
 */
function handleResponseError(instance, error, retryConfig) {
    return __awaiter(this, void 0, void 0, function* () {
        const originalConfig = error.config;
        // Retry logic for transient failures
        if (shouldRetry(error, originalConfig, retryConfig)) {
            originalConfig._retryCount = (originalConfig._retryCount || 0) + 1;
            yield delay(retryConfig.retryDelay * originalConfig._retryCount);
            return instance.request(originalConfig);
        }
        throw yield transformError(error);
    });
}
/**
 * Create an interceptor client with the given configuration.
 * Returns an object with HTTP methods that use the configured axios instance.
 */
function createApiClient(clientConfig = {}) {
    var _a, _b, _c, _d;
    const axiosConfig = {
        baseURL: clientConfig.baseURL || `${config.backendUrl}/api`,
        timeout: clientConfig.timeout || 30000,
        withCredentials: clientConfig.withCredentials !== false,
        headers: Object.assign({ 'Content-Type': 'application/json' }, clientConfig.headers)
    };
    const instance = axios.create(axiosConfig);
    const retryConfig = {
        enabled: (_a = clientConfig.enableRetry) !== null && _a !== void 0 ? _a : true,
        maxRetries: (_b = clientConfig.maxRetries) !== null && _b !== void 0 ? _b : 3,
        retryDelay: (_c = clientConfig.retryDelay) !== null && _c !== void 0 ? _c : 1000,
        retryablePostPrefixes: (_d = clientConfig.retryablePostPrefixes) !== null && _d !== void 0 ? _d : DEFAULT_RETRYABLE_POST_PREFIXES
    };
    setupRequestInterceptors(instance, clientConfig.excludeBasicAuth);
    setupResponseInterceptors(instance, retryConfig);
    return {
        /**
         * Get the underlying Axios instance
         */
        getInstance: () => instance,
        /**
         * Make a GET request
         */
        get: (url, requestConfig) => {
            return instance.get(url, requestConfig);
        },
        /**
         * Make a POST request
         */
        post: (url, data, requestConfig) => {
            return instance.post(url, data, requestConfig);
        },
        /**
         * Make a PUT request
         */
        put: (url, data, requestConfig) => {
            return instance.put(url, data, requestConfig);
        },
        /**
         * Make a PATCH request
         */
        patch: (url, data, requestConfig) => {
            return instance.patch(url, data, requestConfig);
        },
        /**
         * Make a DELETE request
         */
        delete: (url, requestConfig) => {
            return instance.delete(url, requestConfig);
        },
        /**
         * Make a HEAD request
         */
        head: (url, requestConfig) => {
            return instance.head(url, requestConfig);
        },
        /**
         * Make a OPTIONS request
         */
        options: (url, requestConfig) => {
            return instance.options(url, requestConfig);
        }
    };
}
/**
 * Default API client for backend services
 * Uses /api base path with credentials
 */
export const apiClient = createApiClient({
    baseURL: `${config.backendUrl}/api`,
    withCredentials: true,
    timeout: 30000,
    enableRetry: true,
    maxRetries: 3,
    retryDelay: 1000
});
/**
 * FHIR client for FHIR API requests
 * Uses /api/fhir base path with FHIR-specific headers
 */
export const fhirApiClient = createApiClient({
    baseURL: `${config.backendUrl}/api/fhir`,
    withCredentials: true,
    timeout: 30000,
    enableRetry: true,
    maxRetries: 2,
    retryDelay: 500,
    headers: {
        'Content-Type': 'application/fhir+json',
        Accept: 'application/fhir+json'
    }
});
/**
 * SMART Auth client for authentication endpoints
 * Uses /smart base path
 * EXCLUDED from basic auth since it handles its own authentication flow
 */
export const smartAuthClient = createApiClient({
    baseURL: `${config.backendUrl}/smart`,
    withCredentials: true,
    timeout: 15000,
    enableRetry: false,
    excludeBasicAuth: true,
    headers: {
        'Content-Type': 'application/json'
    }
});
/**
 * Check if error is an Axios error
 */
export function isAxiosError(error) {
    return axiosIsAxiosError(error);
}
/**
 * Check if error is an ApiError (from interceptor)
 */
export function isApiError(error) {
    return error instanceof Error && error.name === 'ApiError';
}
/**
 * Extract error message from various error formats.
 * Works with ApiError, AxiosError, Error, or string.
 */
export function extractErrorMessage(error, fallback = 'An error occurred') {
    var _a;
    if (typeof error === 'string') {
        return error;
    }
    if (isApiError(error)) {
        return error.message;
    }
    if (error instanceof Error) {
        return error.message;
    }
    if (isAxiosError(error)) {
        const data = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data;
        return (data === null || data === void 0 ? void 0 : data.message) || (data === null || data === void 0 ? void 0 : data.errorDescription) || (data === null || data === void 0 ? void 0 : data.error) || error.message || fallback;
    }
    return fallback;
}
/**
 * Get HTTP status code from error (if available)
 */
export function getErrorStatusCode(error) {
    var _a;
    if (isApiError(error)) {
        return error.statusCode;
    }
    if (isAxiosError(error)) {
        return (_a = error.response) === null || _a === void 0 ? void 0 : _a.status;
    }
    return undefined;
}
